import { Button, Dropdown, Input } from "@deligoo/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"
import { Redirect } from "react-router-dom"

import { useAppSelector } from "@/App"
import { Site } from "@/sites"
import { setCurrentSite } from "@/store/global"
import { requestUserLogin, selectAuthError, selectIsAuthenticated, selectIsAuthenticating } from "@/store/user"

import cls from "./SignInView.module.scss"

export const SignInView = () => {
  const sites = useAppSelector((state) => state.global.sites, shallowEqual)
  const currentSite = useAppSelector((state) => state.global.currentSite, shallowEqual)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isAuthenticating = useAppSelector(selectIsAuthenticating)

  const dispatch = useDispatch()

  const { t } = useTranslation()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const error = useAppSelector(selectAuthError)

  useEffect(() => {
    if (sites.length && !currentSite) dispatch(setCurrentSite(getDefaultSite(sites)!))
  }, [dispatch, sites, currentSite])

  const handleSiteChange = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    const site = sites.find((site) => site.id === Number(event.currentTarget.value))
    if (site) dispatch(setCurrentSite(site))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (email && password) {
      batch(() => {
        if (currentSite) dispatch(setCurrentSite(currentSite))
        dispatch(requestUserLogin({ username: email, password }))
      })
    }
  }

  if (isAuthenticated) return <Redirect to="/" />

  return (
    <div className={cls.container}>
      <div className={cls.content}>
        <img src="/assets/images/deligoo.svg" alt="DeliGoo" className={cls.logo} />
        <div className={cls.subtitle}>{t("app_title")}</div>
        <form onSubmit={handleSubmit}>
          <h1 className={cls.formTitle}>{t("log_in")}</h1>

          {sites.length > 1 ? (
            <Dropdown
              trigger={
                <span>
                  <Button color="muted" variation="outlined" className="mb-7">
                    {currentSite?.name}
                  </Button>
                </span>
              }
            >
              {sites.map((site) => (
                <Dropdown.MenuItem handleClick={handleSiteChange} value={site.id} key={site.id}>
                  {site.name}
                </Dropdown.MenuItem>
              ))}
            </Dropdown>
          ) : null}

          <Input
            placeholder="E-mail"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value.trim())}
            type="email"
            disabled={isAuthenticating}
            required
            value={email}
            className="mb-7 px-4"
          />

          <Input
            placeholder={t("password")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            type="password"
            disabled={isAuthenticating}
            required
            value={password}
            className="mb-7 px-4"
          />

          <Button
            color="primary"
            type="submit"
            disabled={isAuthenticating || !currentSite}
            raised
            loading={isAuthenticating}
          >
            {t("log_in")}
          </Button>

          {error && <div className={cls.formError}>{error.text}</div>}
        </form>

        {/* <Link to="#">{t("forgot_password")}</Link> */}
      </div>
    </div>
  )
}

function getDefaultSite(sites: Site[]) {
  const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT

  const siteForEnvironment = sites.find((site) => site.name === ENVIRONMENT?.toLowerCase())

  return siteForEnvironment || sites[0]
}
